/*!
 * vue-utils v0.10.20 
 * (c) 2020 Alex Tkachenko
 * Released under the MIT License.
 */
import propsMergerFunction from '@vue/babel-helper-vue-jsx-merge-props';
import { curry } from 'ramda';

function mergeContext() {
  for (var _len = arguments.length, attrs = new Array(_len), _key = 0; _key < _len; _key++) {
    attrs[_key] = arguments[_key];
  }

  return propsMergerFunction(attrs);
}
function renderSlim(nodes, h) {
  var tag = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 'div';
  var data = arguments.length > 3 ? arguments[3] : undefined;

  if (nodes.length === 0) {
    return null;
  }

  if (nodes.length === 1) {
    return nodes[0];
  }

  return h(tag, data, nodes);
}

/* eslint-disable import/prefer-default-export, no-underscore-dangle */
function resolveInjection(key, vm) {
  var source = vm;

  while (source) {
    if (source._provided && Object.prototype.hasOwnProperty.call(source._provided, key)) {
      return source._provided[key];
    }

    source = source.$parent;
  }

  return null;
}

/* eslint-disable import/prefer-default-export, no-underscore-dangle */
var getComponentOptions = function getComponentOptions(Component) {
  return typeof Component === 'function' ? Component.options : Component;
};
var makeF = function makeF(render, props, name) {
  var rest = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : {};
  return Object.assign(rest, {
    name: name,
    functional: true,
    props: props,
    render: render
  });
};

/* eslint-disable prefer-object-spread, no-param-reassign, no-unused-expressions */
var normalizeProps = function normalizeProps(props) {
  if (!props) {
    return {};
  }

  if (Array.isArray(props)) {
    var result = {};
    props.forEach(function (key) {
      if (typeof key === 'string') {
        result[key] = {};
      }
    });
    return result;
  }

  return Object.assign({}, props);
};
var mergeMixinProps = function mergeMixinProps(mixins) {
  var initial = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

  if (!mixins || !mixins.length) {
    return initial;
  }

  return mixins.reduce(function (result, mixin) {
    var props = Object.assign({}, mergeMixinProps(mixin.mixins, result), normalizeProps(mixin.props));
    return Object.assign({}, result, props);
  }, initial);
};
var getProps = function getProps(Component) {
  var options = getComponentOptions(Component);
  var props = normalizeProps(options.props);
  var mixinProps = mergeMixinProps(options.mixins);
  return Object.assign({}, mixinProps, props);
};
var VALUE_TAG_MAP = {
  input: true,
  textarea: true,
  option: true,
  select: true,
  progress: true
};
var DOM_PROP_ATTRS = ['value', 'selected', 'checked', 'muted'];
var mustBeDOM = function mustBeDOM(tag, type, attr) {
  return attr === 'value' && VALUE_TAG_MAP[tag] && type !== 'button' || attr === 'selected' && tag === 'option' || attr === 'checked' && tag === 'input' || attr === 'muted' && tag === 'video';
};
function internalPropsMagicTransform(tag, context) {
  var g = function g(name) {
    return context.props && context.props[name] || context.attrs && context.attrs[name];
  };

  var tp = g('type');
  var hasDom = !!context.domProps;
  DOM_PROP_ATTRS.forEach(function (attr) {
    if (mustBeDOM(tag, tp, attr)) {
      if (hasDom && typeof context.domProps[attr] !== 'undefined') {
        return;
      }

      context.domProps = context.domProps || {};
      context.domProps[attr] = g(attr);
      context.props && delete context.props[attr];
      context.attrs && delete context.attrs[attr];
    }
  });
  return context;
}

/* eslint-disable import/prefer-default-export, no-underscore-dangle, prefer-object-spread */
var withDefaultProps = curry(function (defaults, Component) {
  return makeF(function (h, context) {
    return h(Component, mergeContext(context.data, {
      attrs: context.props
    }), context.children);
  }, Object.keys(defaults).reduce(function (acc, key) {
    acc[key] = {
      default: defaults[key]
    };
    return acc;
  }, getProps(Component)));
});

/* eslint-disable import/prefer-default-export */
function isEmpty(value) {
  return value === null || value === '' || typeof value === 'undefined' || Array.isArray(value) && value.length === 0 || Object(value) === value && Object.keys(value).length === 0;
}

export { DOM_PROP_ATTRS, VALUE_TAG_MAP, getComponentOptions, getProps, internalPropsMagicTransform, isEmpty, makeF, mergeContext, mergeMixinProps, mustBeDOM, normalizeProps, renderSlim, resolveInjection, withDefaultProps };



// WEBPACK FOOTER //
// ./node_modules/@aspectus/vue-utils/dist/vue-utils.esm.js